import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Form,
  InputGroup,
  OverlayTrigger,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  BsBarChartLine,
  BsChatDots,
  BsCheckCircleFill,
  BsChevronDown,
  BsChevronUp,
  BsClipboard,
  BsCodeSlash,
  BsEye,
  BsEyeSlash,
  BsGlobe,
  BsGrid,
  BsSearch,
  BsShieldCheck,
  BsTrash,
  BsXCircleFill,
} from 'react-icons/bs';
import api from '../lib/api';

const WidgetList = ({ detailsRouteBase = '/widgets' }) => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchWidgets();
  }, []);

  const fetchWidgets = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await api.get('/admin/widgets/');
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setWidgets(data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to load widgets.'
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredWidgets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return widgets;

    return widgets.filter((widget) => {
      const name = widget?.name?.toLowerCase() || '';
      const origin = widget?.allowed_origin?.toLowerCase() || '';
      const token = widget?.token?.toLowerCase() || '';
      const brandName = widget?.brand_name?.toLowerCase() || '';

      return (
        name.includes(term) ||
        origin.includes(term) ||
        token.includes(term) ||
        brandName.includes(term)
      );
    });
  }, [widgets, search]);

  const buildEmbedScript = (widgetToken) => {
    const apiBase = process.env.REACT_APP_API_BASE || `${window.location.origin}/api`;
    const backendBase = apiBase.replace(/\/api\/?$/, '');
    return `<script src="${backendBase}/static/chatwidget.js" data-api-base="${apiBase}" data-site-token="${widgetToken}" defer></script>`;
  };

  const copyScript = async (widgetToken) => {
    try {
      await navigator.clipboard.writeText(buildEmbedScript(widgetToken));
      setCopied(widgetToken);
      setTimeout(() => setCopied(null), 1800);
    } catch (err) {
      setError('Unable to copy embed script.');
    }
  };

  const setRowLoading = (id, value) => {
    setActionLoading((prev) => ({ ...prev, [id]: value }));
  };

  const toggleWidget = async (id) => {
    setRowLoading(id, true);
    try {
      await api.post(`/admin/widgets/toggle/${id}/`);
      setWidgets((prev) =>
        prev.map((widget) =>
          widget.id === id
            ? { ...widget, is_active: !widget.is_active }
            : widget
        )
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to update widget status.'
      );
    } finally {
      setRowLoading(id, false);
    }
  };

  const deleteWidget = async (id) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this widget?'
    );
    if (!confirmed) return;

    setRowLoading(id, true);
    try {
      await api.delete(`/admin/widgets/delete/${id}/`);
      setWidgets((prev) => prev.filter((widget) => widget.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to delete widget.'
      );
    } finally {
      setRowLoading(id, false);
    }
  };

  const renderStatusBadge = (isActive) => {
    return isActive ? (
      <Badge
        bg="success"
        className="d-inline-flex align-items-center gap-1 px-3 py-2 rounded-pill"
      >
        <BsCheckCircleFill />
        Active
      </Badge>
    ) : (
      <Badge
        bg="secondary"
        className="d-inline-flex align-items-center gap-1 px-3 py-2 rounded-pill"
      >
        <BsXCircleFill />
        Disabled
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm rounded-4 mt-3">
        <Card.Body className="py-5 d-flex flex-column align-items-center justify-content-center">
          <Spinner animation="border" />
          <div className="mt-3 text-muted">Loading widgets...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm rounded-4 mt-3 overflow-hidden">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
          <div>
            <h5 className="mb-1 fw-bold">Widget List</h5>
            <p className="text-muted mb-0">
              Manage widget status, preview embed scripts, and track activity.
            </p>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
            <InputGroup>
              <InputGroup.Text className="bg-white border-end-0">
                <BsSearch />
              </InputGroup.Text>
              <Form.Control
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by site, token, origin..."
                className="border-start-0"
              />
            </InputGroup>

            <Button
              variant="outline-primary"
              onClick={fetchWidgets}
              className="d-inline-flex align-items-center justify-content-center gap-2"
              style={{ cursor: 'pointer', minWidth: 120 }}
            >
              <BsGrid />
              Refresh
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="danger" className="rounded-4">
            {error}
          </Alert>
        ) : null}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border">
            Total: {widgets.length}
          </Badge>
          <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border">
            Active: {widgets.filter((w) => w.is_active).length}
          </Badge>
          <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border">
            Disabled: {widgets.filter((w) => !w.is_active).length}
          </Badge>
        </div>

        {filteredWidgets.length === 0 ? (
          <div className="border rounded-4 p-5 text-center bg-light-subtle">
            <div className="mb-2 fs-4">
              <BsGrid />
            </div>
            <h6 className="fw-bold mb-1">No widgets found</h6>
            <p className="text-muted mb-0">
              Try a different search term or create a new widget.
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr className="text-nowrap">
                  <th>Site</th>
                  <th>Allowed Origin</th>
                  <th>Token</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWidgets.map((widget) => {
                  const isBusy = !!actionLoading[widget.id];
                  const isExpanded = expanded === widget.id;

                  return (
                    <React.Fragment key={widget.id}>
                      <tr>
                        <td style={{ minWidth: 180 }}>
                          <div className="fw-semibold">{widget.name}</div>
                          {widget.brand_name ? (
                            <small className="text-muted">
                              Brand: {widget.brand_name}
                            </small>
                          ) : null}
                        </td>

                        <td style={{ minWidth: 220 }}>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-primary">
                              <BsGlobe />
                            </span>
                            <span className="text-break">
                              {widget.allowed_origin}
                            </span>
                          </div>
                        </td>

                        <td style={{ minWidth: 180 }}>
                          <code className="small">{widget.token}</code>
                        </td>

                        <td>{renderStatusBadge(widget.is_active)}</td>

                        <td className="text-nowrap">
                          {widget.created_at
                            ? new Date(widget.created_at).toLocaleString()
                            : 'N/A'}
                        </td>

                        <td>
                          <div className="d-flex flex-wrap justify-content-end gap-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Copy embed script</Tooltip>}
                            >
                              <Button
                                size="sm"
                                variant={copied === widget.token ? 'success' : 'outline-primary'}
                                onClick={() => copyScript(widget.token)}
                                className="d-inline-flex align-items-center gap-2"
                                style={{ cursor: 'pointer' }}
                              >
                                <BsClipboard />
                                {copied === widget.token ? 'Copied' : 'Copy'}
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {widget.is_active ? 'Disable widget' : 'Enable widget'}
                                </Tooltip>
                              }
                            >
                              <Button
                                size="sm"
                                variant={widget.is_active ? 'outline-warning' : 'outline-success'}
                                onClick={() => toggleWidget(widget.id)}
                                disabled={isBusy}
                                className="d-inline-flex align-items-center gap-2"
                                style={{ cursor: 'pointer' }}
                              >
                                {widget.is_active ? <BsEyeSlash /> : <BsShieldCheck />}
                                {widget.is_active ? 'Disable' : 'Enable'}
                              </Button>
                            </OverlayTrigger>

                            <Button
                              size="sm"
                              variant="outline-info"
                              onClick={() =>
                                setExpanded(isExpanded ? null : widget.id)
                              }
                              className="d-inline-flex align-items-center gap-2"
                              style={{ cursor: 'pointer' }}
                            >
                              <BsCodeSlash />
                              {isExpanded ? 'Hide' : 'Preview'}
                              {isExpanded ? <BsChevronUp /> : <BsChevronDown />}
                            </Button>

                            <Link
                              to={`${detailsRouteBase}/${widget.token}/analytics`}
                              className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-2"
                              style={{ cursor: 'pointer' }}
                            >
                              <BsBarChartLine />
                              Analytics
                            </Link>

                            <Link
                              to={`${detailsRouteBase}/${widget.token}/chats`}
                              className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-2"
                              style={{ cursor: 'pointer' }}
                            >
                              <BsChatDots />
                              Chats
                            </Link>

                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => deleteWidget(widget.id)}
                              disabled={isBusy}
                              className="d-inline-flex align-items-center gap-2"
                              style={{ cursor: 'pointer' }}
                            >
                              <BsTrash />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td colSpan={6} className="p-0 border-0">
                          <Collapse in={isExpanded}>
                            <div className="px-3 pb-3">
                              <Card className="border-0 bg-light rounded-4 mt-2">
                                <Card.Body className="p-3">
                                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3">
                                    <div>
                                      <h6 className="mb-1 fw-bold">Embed Script Preview</h6>
                                      <p className="text-muted mb-0">
                                        Copy and place this script before the closing
                                        body tag. It loads from backend static files and uses your API base URL.
                                      </p>
                                    </div>

                                    <Button
                                      size="sm"
                                      variant={copied === widget.token ? 'success' : 'primary'}
                                      onClick={() => copyScript(widget.token)}
                                      className="d-inline-flex align-items-center gap-2"
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <BsClipboard />
                                      {copied === widget.token ? 'Copied' : 'Copy Script'}
                                    </Button>
                                  </div>

                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    readOnly
                                    value={buildEmbedScript(widget.token)}
                                    className="font-monospace"
                                  />
                                </Card.Body>
                              </Card>
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default WidgetList;
